import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity, UserPlus, Crown, Calendar, Video, CreditCard, MessageCircle,
  FileText, ShieldCheck, Mail, XCircle, RotateCcw, Loader2, RefreshCw,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_META = {
  signup: { label: "회원가입", icon: UserPlus, color: "text-emerald-600", bg: "bg-emerald-100" },
  membership_upgrade: { label: "정회원 전환", icon: Crown, color: "text-amber-600", bg: "bg-amber-100" },
  event_apply: { label: "이벤트 신청", icon: Calendar, color: "text-blue-600", bg: "bg-blue-100" },
  zoom_apply: { label: "줌미팅 신청", icon: Video, color: "text-indigo-600", bg: "bg-indigo-100" },
  payment: { label: "멤버십 결제", icon: CreditCard, color: "text-burgundy-600", bg: "bg-burgundy-100" },
  consultation: { label: "상담 신청", icon: MessageCircle, color: "text-teal-600", bg: "bg-teal-100" },
  document_submit: { label: "서류인증 신청", icon: FileText, color: "text-violet-600", bg: "bg-violet-100" },
  document_approve: { label: "서류인증 승인", icon: ShieldCheck, color: "text-green-600", bg: "bg-green-100" },
  inquiry: { label: "문의 접수", icon: Mail, color: "text-sky-600", bg: "bg-sky-100" },
  membership_cancel: { label: "멤버십 해지", icon: XCircle, color: "text-stone-500", bg: "bg-stone-200" },
  refund_request: { label: "환불 요청", icon: RotateCcw, color: "text-rose-600", bg: "bg-rose-100" },
};

const DATE_FILTERS = [
  { id: "today", label: "오늘" },
  { id: "yesterday", label: "어제" },
  { id: "7days", label: "최근 7일" },
  { id: "month", label: "이번 달" },
  { id: "custom", label: "직접 선택" },
];

const MEMBERSHIP_LABEL = { basic: "일반", regular: "정회원", premium: "프리미엄", vip: "VIP" };

const STATUS_STYLE = (s) => {
  const v = (s || "").toLowerCase();
  if (["approved", "paid", "completed", "succeeded", "confirmed", "registered"].includes(v)) return "bg-green-100 text-green-700";
  if (["pending", "requested", "submitted", "processing"].includes(v)) return "bg-amber-100 text-amber-700";
  if (["rejected", "cancelled", "failed", "refunded"].includes(v)) return "bg-rose-100 text-rose-700";
  return "bg-stone-100 text-stone-600";
};

function fmtMoney(a, c) {
  if (a === null || a === undefined || a === "") return null;
  const cur = (c || "USD").toUpperCase();
  const sym = cur === "KRW" ? "₩" : cur === "USD" ? "$" : "";
  try { return `${sym}${Number(a).toLocaleString()}${sym ? "" : " " + cur}`; } catch { return `${a} ${cur}`; }
}

export default function AdminActivitySection() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [dateFilter, setDateFilter] = useState("today");
  const [typeFilter, setTypeFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [summary, setSummary] = useState({ total: 0, cards: [] });
  const [logs, setLogs] = useState([]);
  const [logTotal, setLogTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const dateQuery = useCallback(() => {
    let q = `date_filter=${dateFilter}`;
    if (dateFilter === "custom" && startDate) q += `&start=${startDate}${endDate ? `&end=${endDate}` : ""}`;
    return q;
  }, [dateFilter, startDate, endDate]);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [sRes, lRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/activity/summary?${dateQuery()}`, { headers }),
        fetch(`${API_URL}/api/admin/activity/logs?${dateQuery()}&type=${typeFilter}&page=${page}&limit=50`, { headers }),
      ]);
      if (sRes.ok) setSummary(await sRes.json());
      if (lRes.ok) { const d = await lRes.json(); setLogs(d.items || []); setLogTotal(d.total || 0); }
      fetch(`${API_URL}/api/admin/activity/mark-seen`, { method: "POST", headers }).catch(() => {});
    } catch { toast.error("활동 데이터를 불러오지 못했습니다."); }
    finally { setLoading(false); }
  }, [token, dateQuery, typeFilter, page]);

  useEffect(() => { load(); }, [load]);

  const cardLink = (type) => ({
    signup: "/admin/users", membership_upgrade: "/admin/users",
    event_apply: "/admin/events", zoom_apply: "/admin/events",
    payment: "/admin/payments", consultation: "/admin/counseling",
    document_submit: "/admin/document-verification", document_approve: "/admin/document-verification",
    inquiry: "/admin/messages",
  }[type]);

  const grouped = logs.reduce((acc, l) => { (acc[l.kst_date] = acc[l.kst_date] || []).push(l); return acc; }, {});

  return (
    <div className="bg-white rounded-2xl p-5 sm:p-6 shadow-sm border border-stone-200" data-testid="dashboard-recent-activity">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-burgundy-100 flex items-center justify-center">
            <Activity className="w-5 h-5 text-burgundy-600" />
          </div>
          <div>
            <h2 className="font-bold text-stone-800">최근활동</h2>
            <p className="text-xs text-stone-400">회원 활동을 일별로 한눈에 · 한국 시간(KST) 기준</p>
          </div>
        </div>
        <button onClick={() => load()} className="p-2 rounded-lg hover:bg-stone-200 transition-colors" data-testid="activity-refresh-btn">
          <RefreshCw className={`w-5 h-5 text-stone-600 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* Date filter */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {DATE_FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => { setDateFilter(f.id); setPage(1); }}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
              dateFilter === f.id ? "bg-burgundy-600 text-white" : "bg-stone-50 text-stone-600 border border-stone-200 hover:border-burgundy-300"
            }`}
            data-testid={`date-filter-${f.id}`}
          >
            {f.label}
          </button>
        ))}
        {dateFilter === "custom" && (
          <div className="flex items-center gap-2">
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="px-3 py-1.5 rounded-lg border border-stone-200 text-sm" data-testid="custom-start-date" />
            <span className="text-stone-400">~</span>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="px-3 py-1.5 rounded-lg border border-stone-200 text-sm" data-testid="custom-end-date" />
            <button onClick={() => load()} className="px-3 py-1.5 rounded-lg bg-stone-800 text-white text-sm">조회</button>
          </div>
        )}
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-6">
        {summary.cards.map((c) => {
          const meta = TYPE_META[c.type] || {};
          const Icon = meta.icon || Activity;
          const active = typeFilter === c.type;
          return (
            <button
              key={c.type}
              onClick={() => { setTypeFilter(active ? "all" : c.type); setPage(1); }}
              className={`text-left p-4 rounded-2xl border transition-all ${
                active ? "border-burgundy-500 ring-2 ring-burgundy-200 bg-white" : "border-stone-200 bg-stone-50 hover:border-burgundy-300 hover:bg-white"
              }`}
              data-testid={`summary-card-${c.type}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${meta.bg || "bg-stone-100"}`}>
                  <Icon className={meta.color || "text-stone-500"} style={{ width: 18, height: 18 }} />
                </div>
                <span className="text-2xl font-bold text-stone-900" data-testid={`summary-count-${c.type}`}>{c.count}</span>
              </div>
              <p className="text-sm text-stone-600">{c.label}</p>
            </button>
          );
        })}
      </div>

      {/* Type filter chips */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button onClick={() => { setTypeFilter("all"); setPage(1); }} className={`px-3 py-1 rounded-full text-xs font-medium ${typeFilter === "all" ? "bg-stone-800 text-white" : "bg-stone-50 border border-stone-200 text-stone-600"}`} data-testid="type-filter-all">전체</button>
        {Object.entries(TYPE_META).map(([t, m]) => (
          <button key={t} onClick={() => { setTypeFilter(t); setPage(1); }} className={`px-3 py-1 rounded-full text-xs font-medium ${typeFilter === t ? "bg-burgundy-600 text-white" : "bg-stone-50 border border-stone-200 text-stone-600"}`} data-testid={`type-filter-${t}`}>{m.label}</button>
        ))}
      </div>

      {/* Timeline */}
      <div className="border-t border-stone-100 pt-4" data-testid="activity-timeline">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-stone-700 text-sm">활동 타임라인</h3>
          <span className="text-sm text-stone-400">총 {logTotal.toLocaleString()}건{typeFilter !== "all" ? ` · ${TYPE_META[typeFilter]?.label}` : ""}</span>
        </div>

        {loading ? (
          <div className="py-12 text-center text-stone-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : logs.length === 0 ? (
          <div className="py-12 text-center text-stone-400">해당 기간의 활동이 없습니다.</div>
        ) : (
          <div className="space-y-5">
            {Object.entries(grouped).map(([day, items]) => (
              <div key={day}>
                <div className="mb-2">
                  <span className="inline-block px-3 py-1 rounded-full bg-stone-100 text-stone-600 text-xs font-semibold">{day}</span>
                </div>
                <div className="space-y-2">
                  {items.map((l) => {
                    const meta = TYPE_META[l.type] || {};
                    const Icon = meta.icon || Activity;
                    const link = cardLink(l.type);
                    const money = fmtMoney(l.amount, l.currency);
                    return (
                      <div
                        key={l.id}
                        onClick={() => link && navigate(link)}
                        className={`flex gap-3 p-3 rounded-xl border border-stone-100 hover:bg-stone-50 transition-colors ${link ? "cursor-pointer" : ""}`}
                        data-testid={`activity-row-${l.id}`}
                      >
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${meta.bg || "bg-stone-100"}`}>
                          <Icon className={meta.color || "text-stone-500"} style={{ width: 18, height: 18 }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-mono text-stone-400">{l.kst_time}</span>
                            <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${meta.bg || "bg-stone-100"} ${meta.color || "text-stone-600"}`}>{l.type_label}</span>
                            {l.user_membership && l.user_membership !== "basic" && (
                              <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">{MEMBERSHIP_LABEL[l.user_membership] || l.user_membership}</span>
                            )}
                            {l.status && (
                              <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${STATUS_STYLE(l.status)}`}>{l.status}</span>
                            )}
                          </div>
                          <p className="text-sm text-stone-800 mt-1">{l.detail}</p>
                          <div className="flex items-center gap-3 mt-0.5 text-xs text-stone-400 flex-wrap">
                            {l.user_email && <span>{l.user_email}</span>}
                            {l.related_name && <span>· {l.related_name}</span>}
                            {money && <span className="font-semibold text-burgundy-600">· {money}</span>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {logTotal > 50 && (
          <div className="flex items-center justify-center gap-3 mt-6">
            <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="px-4 py-1.5 rounded-lg border border-stone-200 text-sm disabled:opacity-40">이전</button>
            <span className="text-sm text-stone-500">{page} / {Math.ceil(logTotal / 50)}</span>
            <button disabled={page >= Math.ceil(logTotal / 50)} onClick={() => setPage((p) => p + 1)} className="px-4 py-1.5 rounded-lg border border-stone-200 text-sm disabled:opacity-40">다음</button>
          </div>
        )}
      </div>
    </div>
  );
}
